import config from '@/config'
import axios, { AxiosError, AxiosInstance } from 'axios'

const baseUrl = config.service.baseUrl
const siteKey = config.service.siteKey

const accessTokenKey = config.system.accessTokenKey
const refreshTokenKey = config.system.refreshTokenKey

const redirectUrl = undefined
const refreshUrl = `${baseUrl}/authentication-service/api/v1/token/refresh-token`

let isRefreshing = false
let failedQueue: {
  resolve: (value: any) => void
  reject: (error: AxiosError) => void
  config: any
}[] = []
function processQueue(error: AxiosError | null, token: string | null) {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) return reject(error)
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
      axios(config).then(resolve).catch(reject)
    }
  })
  failedQueue = []
}

const axiosClient: AxiosInstance = axios.create({
  baseURL: baseUrl,
  headers: {
    'Content-Type': 'application/json',
    sitekey: siteKey,
  },
  withCredentials: false, // gửi cookie
})

axiosClient.interceptors.request.use((config) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem(accessTokenKey) : null
  if (token && config.headers) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

axiosClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const originalRequest = error.config as any
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest })
        })
      }

      isRefreshing = true

      try {
        const res = await axios.post(
          refreshUrl,
          { refreshToken: localStorage.getItem(refreshTokenKey) },
          {
            withCredentials: true,
            headers: { sitekey: siteKey },
          },
        )

        const newToken = res.data?.data?.accessToken
        const newRefresh = res.data?.data?.refreshToken

        if (newToken) {
          localStorage.setItem(accessTokenKey, newToken)
          localStorage.setItem(refreshTokenKey, newRefresh)
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`
          processQueue(null, newToken)
          return axiosClient(originalRequest)
        } else {
          throw new Error('Token refresh failed')
        }
      } catch (err) {
        processQueue(err as AxiosError, null)
        localStorage.removeItem(accessTokenKey)
        localStorage.removeItem(refreshTokenKey)
        if (typeof window !== 'undefined' && redirectUrl) {
          window.location.href = redirectUrl
        }
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  },
)

export default axiosClient
